import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled, { css } from "styled-components";

import { usePlayerStore } from "../../store/usePlayerStore";
import { focusColor } from "../../styles/variables";

import { handleKeyboardEvent } from "./helpers";

const playerContainerPadding = 20;

const StyledControlBarButton = styled.button<{
  isMobile: boolean;
}>`
  background: transparent;
  border: none;
  color: ${darkTheme.ACTION_PRIMARY};
  cursor: pointer;
  height: ${({ isMobile }) => (isMobile ? "40px" : "32px")};
  margin: 0;
  padding: 0;
  pointer-events: auto;
  position: relative;
  width: ${({ isMobile }) => (isMobile ? "40px" : "32px")};

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
  }
`;

const StyledTooltip = styled.div<{
  visible: boolean;
}>`
  background: rgb(0 0 0 / 79%);
  border-radius: 4px;
  color: ${darkTheme.TEXT_DEFAULT};
  height: 28px;
  line-height: 28px;
  opacity: 0;
  padding: 0 8px;
  position: absolute;
  top: -58px;
  transform: translateY(6px);
  transition: none;
  visibility: hidden;
  white-space: nowrap;
  z-index: 99;

  ${props =>
    props.visible &&
    css`
      opacity: 1;
      transform: translateY(0) !important;
      transition:
        opacity 300ms,
        transform 300ms;
      visibility: visible;
    `}
`;

const Tooltip = ({
  children,
  visible
}: {
  children?: React.ReactNode;
  visible: boolean;
}) => {
  const { useStoreState } = usePlayerStore();
  const playerElement = useStoreState.playerElement();

  const tooltipRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [style, setStyle] = useState<CSSProperties>({});

  const positionTooltip = useCallback(() => {
    if (playerElement && tooltipRef.current) {
      const observer = new IntersectionObserver(nodes => {
        observer.disconnect();

        const playerContainerRect = nodes[0].boundingClientRect;
        const tooltipRect = nodes[1].boundingClientRect;
        const buttonRect = nodes[2].boundingClientRect;
        const buttonRelativePosition = buttonRect.x - playerContainerRect.x;

        let left = -(tooltipRect.width / 2) + buttonRect.width / 2;

        // Adjust if tooltip overflows playerContainer from left side
        if (buttonRelativePosition + left < playerContainerPadding) {
          left += tooltipRect.width / 2 - buttonRelativePosition + 4;
        }

        // Adjust if tooltip overflows playerContainer from right side
        if (
          buttonRelativePosition + left + tooltipRect.width >
          playerContainerRect.width
        ) {
          left -=
            buttonRelativePosition +
            left +
            tooltipRect.width -
            playerContainerRect.width +
            playerContainerPadding;
        }

        setStyle({ left: left + "px" });
      });
      observerRef.current = observer;

      observer.observe(playerElement);
      observer.observe(tooltipRef.current);
      observer.observe(tooltipRef.current.parentElement as HTMLElement);
    }
  }, [playerElement]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener("resize", positionTooltip);

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener("resize", positionTooltip);
    };
  }, [positionTooltip]);

  return (
    <StyledTooltip
      aria-hidden={true}
      ref={tooltipRef}
      style={style}
      visible={visible}>
      {children}
    </StyledTooltip>
  );
};

interface Props {
  children?: React.ReactNode;
  onClick: (event) => void;
  settingsVisible?: boolean;
  tooltip?: string;
}

export const ControlBarButton = forwardRef<HTMLButtonElement, Props>(
  function Button({ children, settingsVisible, tooltip, ...props }, ref) {
    const { useStoreState } = usePlayerStore();
    const device = useStoreState.device();
    const isControlsVisible = useStoreState.isControlsVisible();

    const [tooltipVisible, setTooltipVisible] = useState(false);
    const timerRef = useRef<number>();

    const hideTooltip = useCallback(() => {
      clearTimeout(timerRef.current);
      setTooltipVisible(false);
    }, []);

    const showTooltip = useCallback(() => {
      if (!device.isMobile) {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setTooltipVisible(true);
        }, 500) as unknown as number;
      }
    }, [device]);

    useEffect(() => {
      if (settingsVisible || !isControlsVisible) {
        hideTooltip();
      }
    }, [hideTooltip, isControlsVisible, settingsVisible]);

    useEffect(() => {
      return () => {
        clearTimeout(timerRef.current);
      };
    }, []);

    return (
      <StyledControlBarButton
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onKeyDown={handleKeyboardEvent}
        onKeyUp={handleKeyboardEvent}
        onMouseLeave={hideTooltip}
        onMouseOver={showTooltip}
        ref={ref}
        isMobile={device.isMobile}
        {...props}>
        {children}
        {tooltip && <Tooltip visible={tooltipVisible}>{tooltip}</Tooltip>}
      </StyledControlBarButton>
    );
  }
);
